import React from 'react';
import { nanoid } from 'nanoid';
import { useDialogContext } from './useDialogContext';

export type ModalData<T extends React.ComponentType> = Omit<
    React.ComponentProps<T>,
    'open' | 'onClose' | 'children' | 'defaultOpen' | 'trigger'
> & {
    onClose?: () => void;
};

/**
 * example:
 * ```
 * type MyDialogProps = { field: string, title: string; optional?: boolean };
 * const [showMyDialog] = useDialog(MyDialog, { field });
 * showMyDialog({ title: 'Dialog' }); // show will not ask for `field`, but you can override it
 *
 * ```
 */
export function useDialog<
    T extends React.ComponentType<any>,
    R extends ModalData<T>,
    D extends Partial<R> = NonNullable<unknown>,
>(component: T, data?: D, onClose?: () => void) {
    const key = React.useMemo<string>(nanoid, []);
    const context = useDialogContext();
    const showModal = React.useCallback(
        // TODO: it's not working when calling without modal data as it's optional...
        (modalData?: Omit<R, keyof D>) => {
            context.showModal(key, component, { ...data, ...modalData }, onClose);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, context.showModal]
    );
    const hideModal = React.useCallback(
        () => context.hideModal(key, onClose),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [context.hideModal, onClose, key]
    );

    React.useEffect(
        () => () => {
            context.removeModal(key);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return [showModal, hideModal];
}
